import { useEffect, useCallback, useMemo } from 'react';
import { Video } from '../types';
import { formatViewCount } from '../utils/format';

interface VideoModalProps {
  video: Video | null;
  onClose: () => void;
  allVideos?: Video[];
  onPlayVideo?: (video: Video) => void;
}

/**
 * Modal component for embedded YouTube video playback
 */
export function VideoModal({ video, onClose, allVideos = [], onPlayVideo }: VideoModalProps) {
  // Close on Escape key
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    if (video) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [video, handleKeyDown]);

  // Get related videos (same channel or random from list)
  const relatedVideos = useMemo(() => {
    if (!video || allVideos.length === 0) return [];

    // First, try to find videos from the same channel
    const sameChannel = allVideos.filter(v => v.channelId === video.channelId && v.id !== video.id);

    // Then get other random videos
    const others = allVideos.filter(v => v.channelId !== video.channelId && v.id !== video.id);

    // Combine: up to 2 from same channel, rest random
    const result = [...sameChannel.slice(0, 2)];
    const shuffled = others.sort(() => Math.random() - 0.5);
    result.push(...shuffled.slice(0, 4 - result.length));

    return result.slice(0, 4);
  }, [video, allVideos]);

  if (!video) return null;

  // Use youtube-nocookie.com for privacy-enhanced mode (less bot detection)
  const embedUrl = `https://www.youtube-nocookie.com/embed/${video.id}?rel=0&modestbranding=1&origin=${encodeURIComponent(window.location.origin)}`;

  return (
    <div className="video-modal-overlay" onClick={onClose}>
      <div className="video-modal video-modal-with-related" onClick={(e) => e.stopPropagation()}>
        <button className="video-modal-close" onClick={onClose} title="Close (Esc)">
          ✕
        </button>
        <div className="video-modal-main">
          <div className="video-modal-content">
            <iframe
              src={embedUrl}
              title={video.title}
              allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              referrerPolicy="strict-origin-when-cross-origin"
            />
          </div>
          <div className="video-modal-info">
            <h3 className="video-modal-title">{video.title}</h3>
            <p className="video-modal-channel">{video.channelTitle}</p>
          </div>
        </div>

        {relatedVideos.length > 0 && (
          <div className="video-modal-related">
            <h4 className="related-title">Up Next</h4>
            <div className="related-list">
              {relatedVideos.map(v => (
                <div
                  key={v.id}
                  className="related-item"
                  onClick={() => onPlayVideo?.(v)}
                >
                  <img src={v.thumbnail} alt="" className="related-thumbnail" />
                  <div className="related-info">
                    <div className="related-video-title">{v.title}</div>
                    <div className="related-channel">{v.channelTitle}</div>
                    <div className="related-views">{formatViewCount(v.viewCount)} views</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

